<template>
    <Headify :data="headMeta" />

    <div class="bg-gray-100">
        <NavMain :key="route.name" />

        <div class="flex flex-1 flex-col pb-24 lg:pl-64">
            <main class="flex-1">
                <div class="mx-auto w-full max-w-7xl px-2 pt-14 sm:px-4 lg:pt-4">
                    <div
                        class="cursor-default rounded-md bg-slate-900 bg-bottom p-6 text-center shadow-sm select-none"
                        style="background-image: url('/theme/default/lgbg.jpg')"
                    >
                        <div class="px-2 py-3 text-xl font-medium text-slate-300">
                            {{ t('welcomeText') }}
                        </div>
                        <h1
                            class="px-2 py-3 text-3xl font-bold tracking-wide text-slate-200 uppercase"
                        >
                            {{ t('appName') }}
                        </h1>
                    </div>

                    <dl class="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <div class="order-2 rounded-md bg-white shadow-sm md:order-1">
                            <dt
                                class="flex cursor-default rounded-t-md bg-gray-200 font-medium select-none"
                            >
                                <div class="rounded-md p-3 text-gray-600">
                                    <MdiServerNetwork class="-mt-1 inline h-5 w-5 text-blue-600" />
                                    {{ stats[0].name }}
                                </div>
                            </dt>
                            <dd class="min-h-[120px] px-6 py-px">
                                <div class="my-1 flex items-center">
                                    <div
                                        v-clipboard:copy="copyText.be"
                                        v-clipboard:success="copyHandlerSuccess"
                                        v-clipboard:error="copyHandlerError"
                                        class="cursor-help p-1 text-sm"
                                        :title="t('copyToClipboard')"
                                    >
                                        <span class="font-medium text-slate-800">
                                            <MdiWeb class="-mt-1 inline h-5 w-5 text-emerald-700" />
                                            {{ config.env.apiUrl }}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-for="(stat, index) in stats[0].data"
                                    :key="index"
                                    class="my-1 flex items-center"
                                >
                                    <div class="p-1 text-sm">
                                        <span class="font-normal text-emerald-700">
                                            {{ index }}:
                                        </span>
                                        <span class="font-medium text-slate-800">{{ stat }}</span>
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div class="order-1 rounded-md bg-white shadow-sm md:order-2">
                            <dt
                                class="flex cursor-default rounded-t-md bg-gray-200 font-medium select-none"
                            >
                                <div class="rounded-md p-3 text-gray-600">
                                    <MdiFileChart class="-mt-1 inline h-5 w-5 text-blue-600" />
                                    {{ stats[2].name }}
                                </div>
                            </dt>
                            <dd class="flex min-h-[120px] items-baseline px-6 py-px">
                                <Doughnut
                                    :data="stats[2].data.chart"
                                    :options="stats[2].data.options"
                                    class="mt-2"
                                />
                            </dd>
                        </div>
                        <div class="order-3 rounded-md bg-white shadow-sm">
                            <dt
                                class="flex cursor-default rounded-t-md bg-gray-200 font-medium select-none"
                            >
                                <div class="rounded-md p-3 text-gray-600">
                                    <MdiServerNetwork class="-mt-1 inline h-5 w-5 text-blue-600" />
                                    {{ stats[1].name }}
                                </div>
                            </dt>
                            <dd class="min-h-[120px] px-6 py-px">
                                <div class="my-1 flex items-center">
                                    <div
                                        v-clipboard:copy="copyText.fs"
                                        v-clipboard:success="copyHandlerSuccess"
                                        v-clipboard:error="copyHandlerError"
                                        class="cursor-help p-1 text-sm"
                                        :title="t('copyToClipboard')"
                                    >
                                        <span
                                            :class="[
                                                !config.env.fileStorageEnabled
                                                    ? 'text-red-800'
                                                    : 'text-slate-800',
                                                'font-medium',
                                            ]"
                                        >
                                            <MdiWeb
                                                :class="[
                                                    !config.env.fileStorageEnabled
                                                        ? 'text-red-800'
                                                        : 'text-emerald-700',
                                                    '-mt-1 inline h-5 w-5',
                                                ]"
                                            />
                                            {{ config.env.fileStorageApiUrl }}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-for="(stat, index) in stats[1].data"
                                    :key="index"
                                    class="my-1 flex items-center"
                                >
                                    <div class="p-1 text-sm">
                                        <span class="font-normal text-emerald-700"
                                            >{{ index }}:
                                        </span>
                                        <span class="font-medium text-slate-800">{{ stat }}</span>
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div
                            class="order-4 min-h-[150px] rounded-md bg-white shadow-sm sm:col-span-2 lg:col-span-3"
                        >
                            <dt
                                class="flex cursor-default rounded-tl-md bg-gray-200 font-medium select-none"
                            >
                                <div class="rounded-md p-3 text-gray-600">
                                    <MdiCodeBraces class="-mt-1 inline h-5 w-5 text-blue-600" />
                                    {{ stats[3].name }}
                                </div>
                            </dt>
                            <dd class="min-h-[70px] px-3 py-px">
                                <Bar
                                    v-if="stats[3].data?.chart"
                                    :data="stats[3].data.chart"
                                    :options="stats[3].data.options"
                                    height="100"
                                    class="mt-2"
                                />
                            </dd>
                        </div>
                        <div
                            class="order-4 min-h-[150px] rounded-md bg-white shadow-sm sm:col-span-2 lg:col-span-3"
                        >
                            <dt
                                class="flex cursor-default rounded-tr-md bg-gray-200 font-medium select-none"
                            >
                                <div class="rounded-md p-3 text-gray-600">
                                    <MdiRepeat class="-mt-1 inline h-5 w-5 text-emerald-600" />
                                    {{ stats[4].name }}
                                </div>
                            </dt>
                            <dd class="min-h-[70px] px-3 py-px">
                                <Bar
                                    v-if="stats[4].data?.chart"
                                    :data="stats[4].data.chart"
                                    :options="stats[4].data.options"
                                    height="100"
                                    class="mt-2"
                                />
                            </dd>
                        </div>
                    </dl>
                </div>
            </main>
        </div>
    </div>

    <Spinner
        v-if="pageControl.loading === true"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, inject, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { t } from '../composables/i18n';
import Headify from '../components/Headify.vue';
import NavMain from '../ui/NavMain.vue';
import Spinner from '../ui/Spinner.vue';
import navs from '../composables/navs';
import { getBackendServerInfo, getFileStorageServerInfo } from '../composables/useStats';
import { getTemplates } from '../composables/useTemplates';
import { listPlaceholders } from '../composables/usePlaceholders';
import { notify } from 'notiwind';
import {
    Chart as ChartJS,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import { Bar, Doughnut } from 'vue-chartjs';
import {
    MdiFileChart,
    MdiServerNetwork,
    MdiRepeat,
    MdiCodeBraces,
    MdiWeb,
} from 'materialdesignicons-vue3/icons/';
import { asleep } from '../composables/utils';

const config = inject('config');
const route = useRoute();

const pageControl = reactive({
    loading: true,
});

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getAllPlaceholders = async (type) => {
    return (
        (await listPlaceholders({
            type: type,
        })) || { items: [], totalCount: 0 }
    );
};

const emptyChartData = () => ({
    chart: { labels: [], datasets: [] },
    options: {},
});

const stats = reactive([
    {
        name: t('backendServerNote'),
        data: [],
    },
    {
        name: t('fileServerNote'),
        data: [],
    },
    {
        name: t('templateStatsNote'),
        data: emptyChartData(),
    },
    {
        name: t('placeholderSimpleStatsNote'),
        data: emptyChartData(),
    },
    {
        name: t('placeholderMultipleStatsNote'),
        data: emptyChartData(),
    },
]);

const sources = reactive({
    backend: [],
    fileStorage: [],
    templates: {
        pdf: {
            items: [],
            totalCount: 0,
        },
        email: {
            items: [],
            totalCount: 0,
        },
        sms: {
            items: [],
            totalCount: 0,
        },
    },
    placeholders: {
        simple: { items: [], totalCount: 0 },
        multiple: { items: [], totalCount: 0 },
        //imageCode: { items: [], totalCount: 0 }, // TODO
    },
});

const refreshSources = async () => {
    sources.backend = await getBackendServerInfo();
    if (config.env.fileStorageEnabled) {
        sources.fileStorage = await getFileStorageServerInfo();
    }
    sources.templates.pdf = (await getTemplates({
        offset: 0,
        limit: 1,
        type: 'PDF',
    })) || {
        items: [],
        totalCount: 0,
    };
    sources.templates.email = (await getTemplates({
        offset: 0,
        limit: 1,
        type: 'EMAIL',
    })) || {
        items: [],
        totalCount: 0,
    };
    sources.templates.sms = (await getTemplates({
        offset: 0,
        limit: 1,
        type: 'SMS',
    })) || {
        items: [],
        totalCount: 0,
    };
    sources.placeholders.simple = (await getAllPlaceholders('SIMPLE')) || {
        items: [],
        totalCount: 0,
    };
    sources.placeholders.multiple = (await getAllPlaceholders('MULTIPLE')) || {
        items: [],
        totalCount: 0,
    };
    /* sources.placeholders.imageCode = (await getAllPlaceholders('IMAGE_CODE')) || {
        items: [],
        totalCount: 0,
    }; */
};

const label = (tooltipItems) => {
    return ` ${t('items', { count: tooltipItems.parsed })} `;
};

const getInnerLabel = () => {
    const middleTextData = reactive({
        text:
            (
                sources.templates.pdf.totalCount +
                sources.templates.email.totalCount +
                sources.templates.sms.totalCount
            ).toString() || '0',
    });
    const innerLabel = {
        id: 'innerlabel',
        //afterDatasetDraw(chart, args, pluginOptions) {
        afterDatasetDraw(chart, args) {
            if (args.meta.type === 'doughnut') {
                const { ctx } = chart;
                const xa = args.meta.data[0].x;
                const ya = args.meta.data[0].y;
                const middleText = middleTextData.text;
                const fontSize = 36;
                ctx.save();
                ctx.textAlign = 'center';
                ctx.font = `${fontSize}px sans-serif`;
                ctx.fillText(middleText, xa, ya + fontSize / 4);
                ctx.restore();
            }
        },
    };
    return innerLabel;
};

const calculateTemplateStats = (templates) => {
    const calc = countTemplateTypes(templates);
    const result = {
        chart: {
            labels: ['PDF', 'EMAIL', 'SMS' /* 'INBOX' */],
            datasets: [
                {
                    backgroundColor: [
                        'rgb(244 63 94)',
                        'rgb(59 130 246)',
                        'rgb(245 158 11)',
                        /* 'rgb(16 185 129)', */
                    ],
                    data: Object.values(calc),
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                },
                tooltip: {
                    callbacks: {
                        label: label,
                    },
                },
            },
        },
    };
    return result;
};

const countTemplateTypes = (templates) => {
    const counts = {
        PDF: templates.pdf.totalCount || 0,
        EMAIL: templates.email.totalCount || 0,
        SMS: templates.sms.totalCount || 0,
    };
    return counts;
};

const calculatePlaceholdersByType = (placeholderList, chartConfig) => {
    const result = {
        chart: {
            labels: [],
            datasets: [
                {
                    label: chartConfig.label,
                    backgroundColor: chartConfig.backgroundColor,
                    data: [],
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                },
            },
            scales: {
                y: {
                    ticks: {
                        beginAtZero: true,
                    },
                },
                x: {
                    ticks: {
                        maxRotation: 80,
                    },
                },
            },
        },
    };
    if (!placeholderList.items || placeholderList.totalCount === 0) {
        return result;
    }
    const labels = [];
    const data = [];
    placeholderList.items.forEach((placeholder) => {
        labels.push(placeholder.name);
        data.push(placeholder.model?.length || 0);
    });
    result.chart.labels = labels;
    result.chart.datasets[0].data = data;
    return result;
};

const getStats = async () => {
    await refreshSources();
    stats[0].data = sources.backend?.build || [];
    stats[1].data = sources.fileStorage?.build || [];
    stats[2].data = calculateTemplateStats(sources.templates);
    stats[3].data = calculatePlaceholdersByType(sources.placeholders.simple, {
        label: ` ${t('placeholderSimpleStatsNote')}`,
        backgroundColor: 'rgb(59 130 246)',
    });
    stats[4].data = calculatePlaceholdersByType(sources.placeholders.multiple, {
        label: ` ${t('placeholderMultipleStatsNote')}`,
        backgroundColor: 'rgb(16 185 129)',
    });
    pageControl.loading = false;
};

const copyText = reactive({
    be: config.env.apiUrl,
    fs: config.env.fileStorageApiUrl,
});

const copyHandlerSuccess = async (capture, state = 'info') => {
    notify(
        {
            group: state,
            title: t('copiedToClipboard'),
            text: capture.text,
        },
        1770,
    );
};
const copyHandlerError = async (capture) => {
    notify(
        {
            group: 'error',
            title: t('Error'),
            text: capture.text,
        },
        2500,
    );
};

const headMeta = navs().metaFind(route.name);

onMounted(async () => {
    await asleep(700);
    await getStats();
    ChartJS.register(getInnerLabel());
});
</script>
